import { lazy } from "preact/compat";
import { Route } from "react-router";
import { TwitchCallbackPage } from "react-twitch-implicit-auth";
import { AnalyticsTracking } from "@/components/layout/analytics-tracking.jsx";
import GlobalErrorBoundary from "@/components/layout/global-error-boundary.jsx";
import Layout from "@/components/layout/layout.jsx";
import ScheduleLayout from "@/components/page-layouts/schedule.page-layout.jsx";
import { chatbots } from "@/data/chatbots.js";
import { dashboards } from "@/data/dashboards.jsx";
import { graphics } from "@/data/graphics.js";
import { information } from "@/data/information.js";
import { overlays } from "@/data/overlays.jsx";
import { SetPage } from "@/util/analyticsHelper.js";
import { getTypeSlug } from "@/util/common.js";
import TiltifyCallback from "./misc/tiltify-callback.jsx";
import TwitchChatbotCallbackPage from "./misc/twitch-chatbot-callback.jsx";

const DonationTrainOverlayPage = lazy(
  () => import("./overlays/donation-train/donation-train-overlay.page.jsx"),
);
const MaxProgressBarPage = lazy(() => import("./progress-bars/max-progress-bar.page.jsx"));

// Misc. Pages
const PageNotFound = lazy(() => import("./not-found.jsx"));
const Home = lazy(() => import("./home.page.jsx"));

// Layout
const CardPageLayout = lazy(() => import("@/components/page-layouts/card.page-layout.jsx"));
const ChatbotPageLayout = lazy(() => import("@/components/page-layouts/chatbot.page-layout.jsx"));
const GraphicsBundlePageLayout = lazy(
  () => import("@/components/page-layouts/graphics-bundle.page-layout.jsx"),
);
const InformationPageLayout = lazy(
  () => import("@/components/page-layouts/information.page-layout.jsx"),
);
const ListPageLayout = lazy(() => import("@/components/page-layouts/list.page-layout.jsx"));
const OverlayPageLayout = lazy(() => import("@/components/page-layouts/overlay.page-layout.jsx"));
const DashboardPageLayout = lazy(
  () => import("@/components/page-layouts/dashboard.page-layout.jsx"),
);

// Chatbot Pages
const DiscordChatbotPage = lazy(() => import("./chatbots/discord-chatbot.page.jsx"));
const TwitchChatbotPage = lazy(() => import("./chatbots/twitch-chatbot.page.jsx"));

// Information Pages
const BestPracticesPage = lazy(() => import("./information/best-practices.page.jsx"));
const MatchingPage = lazy(() => import("./information/matching.page.jsx"));
const EveryDollarCountsPage = lazy(
  () => import("./information/every-dollar-counts/every-dollar-counts.page.jsx"),
);

// Schedule Pages
const Schedule = lazy(() => import("./schedule/page.jsx"));

// Overlay Pages
const AlertsOverlayPage = lazy(() => import("./overlays/alerts/alerts-overlay.page.jsx"));
const ArtSpaceOverlayPage = lazy(() => import("./overlays/art-space/art-space-overlay.page.jsx"));
const BallPitOverlayPage = lazy(() => import("./overlays/ball-pit/ball-pit-overlay.page.jsx"));
const DashboardPage = lazy(() => import("./overlays/dashboard.page.jsx"));

const DonorListOverlayPage = lazy(() => import("./overlays/donor-list-overlay.page.jsx"));
const GiftsThatGiveOverlayPage = lazy(
  () => import("./overlays/gifts-that-give/gifts-that-give.page.jsx"),
);
const LeaderboardOverlayPage = lazy(() => import("./overlays/leaderboard-overlay.page.jsx"));
const MilestoneOverlayPage = lazy(() => import("./overlays/milestone-overlay.page.jsx"));
const PollOverlayPage = lazy(() => import("./overlays/poll-overlay.page.jsx"));
const RewardsOverlayPage = lazy(() => import("./overlays/rewards-overlay.page.jsx"));
const TextToSpeechPage = lazy(() => import("./overlays/text-to-speech/text-to-speech.page.jsx"));
const TotalOverlayPage = lazy(() => import("./overlays/total-overlay.page.jsx"));

const LifetimeRaisedPage = lazy(() => import("./overlays/lifetime-raised.page.jsx"));

const PreviousTotalsOverlayPage = lazy(() => import("./overlays/previous-years-overlay.page.jsx"));

const SubathonTimerPage = lazy(() => import("./overlays/subathon-timer/subathon-timer.page.jsx"));

const BasicProgressBarPage = lazy(() => import("./progress-bars/basic-progress-bar.page.jsx"));
const NewProgressBarPage = lazy(() => import("./progress-bars/new-progress-bar.page.jsx"));

const MinProgressBarPage = lazy(() => import("./progress-bars/min-progress-bar.page.jsx"));

const VerticalProgressBarPage = lazy(
  () => import("./progress-bars/vertical-progress-bar.page.jsx"),
);

const GCXSelfServicePage = lazy(() => import("./gcx/gcx-self-service.page.jsx"));

const FriendsgivingSelfServicePage = lazy(
  () => import("./friendsgiving/friendsgiving-self-service.page.jsx"),
);

const CCAMToolsPage = lazy(() => import("./ccam-tools.page.jsx"));

const loader = async (args) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const pageURL = new URL(args.request.url);
      const pathName = pageURL.pathname;

      const { type, slug } = getTypeSlug(pathName);
      let pageType;
      let data;

      switch (type) {
        case "overlays":
          data = overlays;
          pageType = "Overlays";
          break;
        case "chatbots":
          data = chatbots;
          pageType = "Chatbots";
          break;
        case "graphics":
          data = graphics;
          pageType = "Graphics";
          break;
        case "information":
          data = information;
          pageType = "Information";
          break;
        case "dashboards":
          data = dashboards;
          pageType = "Dashboards";
          break;
      }

      if (data && slug && data[slug]) {
        const { name } = data[slug];

        let fixedName = name;

        if (!name.includes("Page")) {
          fixedName = `${name} Page`;
        }

        SetPage(`PL Toolkit:${fixedName}`, pathName);
      } else if (pageType) {
        SetPage(`PL Toolkit:${pageType} Page`, pathName);
      } else {
        SetPage(`PL Toolkit:Home Page`, pathName);
      }

      resolve(window.stjude.digitalData);
    }, 10);
  });
};

export default function Routes() {
  return (
    <Route
      errorElement={<GlobalErrorBoundary />}
      loader={loader}
      shouldRevalidate={({ currentUrl, nextUrl }) => {
        return currentUrl !== nextUrl;
      }}
      element={<AnalyticsTracking />}
      hydrateFallbackElement={<div></div>}
    >
      <Route path="/chatbot-landing" element={<TwitchChatbotCallbackPage />} />
      <Route path="/twitch-callback" element={<TwitchCallbackPage />} />
      <Route path="/tiltify-callback" element={<TiltifyCallback />} />
      <Route path="/schedule/*" element={<ScheduleLayout />}>
        <Route index element={<Schedule />} />
        <Route path=":date" element={<Schedule />} />
      </Route>
      <Route path="/*" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="ccam" element={<CCAMToolsPage />} />
        <Route path="gcx" element={<GCXSelfServicePage />} />
        <Route path="friendsgiving" element={<FriendsgivingSelfServicePage />} />
        <Route path="ccam" element={<CCAMToolsPage />} />
        <Route path="graphics/" element={<CardPageLayout />} />
        <Route path="graphics/*" element={<GraphicsBundlePageLayout />} />
        <Route path="chatbots/" element={<ListPageLayout />} />
        <Route path="chatbots/*" element={<ChatbotPageLayout />}>
          <Route path="discord" element={<DiscordChatbotPage />} />
          <Route path="twitch" element={<TwitchChatbotPage />} />
        </Route>
        <Route path="information/" element={<ListPageLayout />} />
        <Route path="information/*" element={<InformationPageLayout />}>
          <Route path="best-practices" element={<BestPracticesPage />} />
          <Route path="matching" element={<MatchingPage />} />
          <Route path="every-dollar-makes-a-difference" element={<EveryDollarCountsPage />} />
        </Route>
        <Route path="dashboards/" element={<CardPageLayout />} />
        <Route path="dashboards/*" element={<DashboardPageLayout />}>
          <Route path="fundraising" element={<DashboardPage />} />
        </Route>

        <Route path="overlays/" element={<CardPageLayout />} />
        <Route path="overlays/*" element={<OverlayPageLayout />}>
          <Route path="alerts" element={<AlertsOverlayPage />} />
          <Route path="ball-pit" element={<BallPitOverlayPage />} />
          <Route path="art-space" element={<ArtSpaceOverlayPage />} />
          <Route path="donorlist" element={<DonorListOverlayPage />} />
          <Route path="leaderboard" element={<LeaderboardOverlayPage />} />
          <Route path="rewards" element={<RewardsOverlayPage />} />
          <Route path="lifetime" element={<LifetimeRaisedPage />} />
          <Route path="polls" element={<PollOverlayPage />} />
          <Route path="milestone" element={<MilestoneOverlayPage />} />
          <Route path="total" element={<TotalOverlayPage />} />
          <Route path="donation-train" element={<DonationTrainOverlayPage />} />
          <Route path="subathon-timer" element={<SubathonTimerPage />} />
          <Route path="previous-totals" element={<PreviousTotalsOverlayPage />} />
          <Route path="gifts-that-give" element={<GiftsThatGiveOverlayPage />} />
          <Route path="tts" element={<TextToSpeechPage />} />
          <Route path="basic" element={<BasicProgressBarPage />} />
          <Route path="horizontal" element={<NewProgressBarPage />} />
          <Route path="vertical" element={<VerticalProgressBarPage />} />
          <Route path="max" element={<MaxProgressBarPage />} />
          <Route path="min" element={<MinProgressBarPage />} />
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Route>
  );
}
